var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { createContext, useCallback, useContext, useState, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { confirm } from '../../core/components/Confirm';
import { PageTypes } from '../../social/constants';
let defaultValue = {};
const defaultAskForConfirmation = (_a) => {
    var { onSuccess: onOk } = _a, params = __rest(_a, ["onSuccess"]);
    return confirm(Object.assign(Object.assign({}, params), { onOk }));
};
export const defaultNavigationBlocker = {
    title: React.createElement(FormattedMessage, { id: "navigationBlocker.title" }),
    content: React.createElement(FormattedMessage, { id: "navigationBlocker.content" }),
    okText: React.createElement(FormattedMessage, { id: "navigationBlocker.okText" }),
};
if (process.env.NODE_ENV !== 'production') {
    defaultValue = {
        page: { type: PageTypes.NewsFeed },
        onChangePage: (type) => console.log(`NavigationContext onChangePage(${type})`),
        onClickCategory: (categoryId) => console.log(`NavigationContext onClickCategory(${categoryId})`),
        onClickCommunity: (communityId) => console.log(`NavigationContext onClickCommunity(${communityId})`),
        onClickUser: (userId) => console.log(`NavigationContext onClickUser(${userId})`),
        onCommunityCreated: (communityId) => console.log(`NavigationContext onCommunityCreated(${communityId})`),
        onEditCommunity: (communityId) => console.log(`NavigationContext onEditCommunity({${communityId})`),
        onEditUser: (userId) => console.log(`NavigationContext onEditUser(${userId})`),
        onMessageUser: (userId) => console.log(`NavigationContext onMessageUser(${userId})`),
    };
}
export const NavigationContext = createContext(defaultValue);
export const useNavigation = () => useContext(NavigationContext);
export default ({ askForConfirmation = defaultAskForConfirmation, children, onChangePage: onChangePageProp, onClickCategory, onClickCommunity, onClickUser, onCommunityCreated, onEditCommunity, onEditUser, onMessageUser, }) => {
    const [pages, setPages] = useState([{ type: PageTypes.NewsFeed }]);
    const [navigationBlocker, setNavigationBlocker] = useState();
    const confirmPageChange = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        if (navigationBlocker) {
            // for more info about this, see https://ekoapp.atlassian.net/browse/UP-3462?focusedCommentId=77155
            return new Promise((resolve) => {
                askForConfirmation(Object.assign(Object.assign({}, navigationBlocker), { onSuccess: () => {
                        setNavigationBlocker(undefined);
                        resolve(true);
                    }, onCancel: () => resolve(false) }));
            });
        }
        return true;
    }), [askForConfirmation, navigationBlocker]);
    const pushPage = useCallback((newPage) => __awaiter(void 0, void 0, void 0, function* () {
        if (!(yield confirmPageChange()))
            return;
        setPages((prevState) => [...prevState, newPage]);
    }), [confirmPageChange]);
    const popPage = () => {
        setPages((prevState) => (prevState.length > 1 ? prevState.slice(0, -1) : prevState));
    };
    const onChangePage = useMemo(() => {
        if (!onChangePageProp)
            return undefined;
        return (...args) => __awaiter(void 0, void 0, void 0, function* () {
            if (!(yield confirmPageChange()))
                return;
            onChangePageProp(args);
        });
    }, [onChangePageProp, confirmPageChange]);
    const handleChangePage = useCallback((type) => {
        if (onChangePage)
            return onChangePage({ type });
        pushPage({ type });
    }, [onChangePage, pushPage]);
    const handleClickCommunity = useCallback((communityId) => {
        const next = {
            type: PageTypes.CommunityFeed,
            communityId,
        };
        if (onChangePage)
            return onChangePage(next);
        if (onClickCommunity)
            return onClickCommunity(communityId);
        console.log('handleClickCommunity', { communityId });
        pushPage(next);
    }, [onChangePage, onClickCommunity, pushPage]);
    const handleCommunityCreated = useCallback((communityId) => {
        const next = {
            type: PageTypes.CommunityFeed,
            communityId,
            isNewCommunity: true,
        };
        if (onChangePage)
            return onChangePage(next);
        if (onCommunityCreated)
            return onCommunityCreated(communityId);
        console.log('handleCommunityCreated', { communityId });
        pushPage(next);
    }, [onChangePage, onCommunityCreated, pushPage]);
    const handleClickCategory = useCallback((categoryId) => {
        const next = {
            type: PageTypes.Category,
            categoryId,
        };
        if (onChangePage)
            return onChangePage(next);
        if (onClickCategory)
            return onClickCategory(categoryId);
        console.log('handleClickCategory', { categoryId });
        pushPage(next);
    }, [onChangePage, onClickCategory, pushPage]);
    const handleClickUser = useCallback((userId, pageType) => {
        const next = {
            type: pageType !== null && pageType !== void 0 ? pageType : PageTypes.UserFeed,
            userId,
        };
        if (onChangePage)
            return onChangePage(next);
        if (onClickUser)
            return onClickUser(userId);
        console.log('handleClickUser', { userId });
        pushPage(next);
    }, [onChangePage, onClickUser, pushPage]);
    const handleEditUser = useCallback((userId) => {
        const next = {
            type: PageTypes.UserEdit,
            userId,
        };
        if (onChangePage)
            return onChangePage(next);
        if (onEditUser)
            return onEditUser(userId);
        console.log('handleEditUser', { userId });
        pushPage(next);
    }, [onChangePage, onEditUser, pushPage]);
    const handleEditCommunity = useCallback((communityId, tab) => {
        const next = {
            type: PageTypes.CommunityEdit,
            communityId,
            tab,
        };
        if (onChangePage)
            return onChangePage(next);
        if (onEditCommunity)
            return onEditCommunity(communityId, { tab });
        console.log('handleEditCommunity', { communityId, tab });
        pushPage(next);
    }, [onChangePage, onEditCommunity, pushPage]);
    const handleMessageUser = useCallback((userId) => {
        const next = {
            type: 'conversation',
            userId,
        };
        if (onChangePage)
            return onChangePage(next);
        if (onMessageUser)
            return onMessageUser(userId);
        console.log('handleMessageUser', { userId });
        // pushPage(next);
    }, [onChangePage, onMessageUser]);
    const value = useMemo(() => ({
        page: pages[pages.length - 1],
        onChangePage: handleChangePage,
        onClickCategory: handleClickCategory,
        onClickCommunity: handleClickCommunity,
        onClickUser: handleClickUser,
        onCommunityCreated: handleCommunityCreated,
        onEditCommunity: handleEditCommunity,
        onEditUser: handleEditUser,
        onMessageUser: handleMessageUser,
        onBack: popPage,
        setNavigationBlocker,
    }), [
        handleChangePage,
        handleClickCategory,
        handleClickCommunity,
        handleClickUser,
        handleCommunityCreated,
        handleEditCommunity,
        handleEditUser,
        handleMessageUser,
        pages,
    ]);
    return React.createElement(NavigationContext.Provider, { value: value }, children);
};
//# sourceMappingURL=NavigationProvider.js.map